import { useCallback, useEffect, useState } from "react";
import { portalFetch } from "../api/client";
import type { PortalPreviewDto } from "../model";
import { launchPortalPreview } from "../preview/launcher";
import { StatusBanner } from "./StatusBanner";
export function PreviewView({
	projectId,
	available = true,
	readyForDelivery = true,
	waitingForAdmin = false,
}: {
	projectId: string;
	available?: boolean;
	readyForDelivery?: boolean;
	waitingForAdmin?: boolean;
}) {
	const [items, setItems] = useState<PortalPreviewDto[]>([]);
	const [error, setError] = useState("");
	const [feedback, setFeedback] = useState<Record<string, string>>({});
	const [status, setStatus] = useState("");
	const load = useCallback(async () => {
		const page = await portalFetch<{ items: PortalPreviewDto[] }>(
			`/api/portal/v1/projects/${projectId}/previews?limit=50&offset=0`,
		);
		setItems(page.items);
	}, [projectId]);
	useEffect(() => {
		load();
	}, [load]);
	async function request() {
		setError("");
		try {
			await portalFetch(`/api/portal/v1/projects/${projectId}/previews/request`, { method: "POST", body: "{}" });
			await load();
		} catch {
			setError(
				"Preview startup is not enabled for this project yet. A workspace administrator can enable it in Command Center.",
			);
		}
	}
	async function launch(previewId: string) {
		try {
			const data = await portalFetch<{ previewId: string; exchangeCode: string; previewOrigin: string }>(
				`/api/portal/v1/projects/${projectId}/previews/${previewId}/launch`,
				{ method: "POST", body: "{}" },
			);
			await launchPortalPreview(data);
		} catch {
			setError("The private preview relay is unavailable. Your project and conversation are still available.");
		}
	}
	async function sendFeedback(previewId: string) {
		try {
			await portalFetch(`/api/portal/v1/projects/${projectId}/previews/${previewId}/feedback`, {
				method: "POST",
				body: JSON.stringify({ body: feedback[previewId]?.trim() }),
			});
			setFeedback((current) => ({ ...current, [previewId]: "" }));
			setStatus("Preview feedback sent.");
		} catch {
			setStatus("Preview feedback could not be sent.");
		}
	}
	return (
		<section aria-labelledby="preview-heading">
			<h2 id="preview-heading">Private preview</h2>
			<p>Preview code runs on a separate protected origin and cannot access your portal session.</p>
			{!readyForDelivery ? (
				<StatusBanner kind="info">
					{waitingForAdmin
						? "Waiting for administrator review. Preview unlocks after the revised spec is approved; no action is required from you right now."
						: "Preview becomes available after the living spec is complete and a workspace administrator approves it for delivery."}
				</StatusBanner>
			) : !available ? (
				<StatusBanner kind="info">
					Preview delivery is visible here but is not configured on the server yet.
				</StatusBanner>
			) : null}
			{error && <StatusBanner kind="error">{error}</StatusBanner>}
			<button type="button" onClick={request} disabled={!available || !readyForDelivery}>
				Request preview
			</button>
			{status && <StatusBanner kind={status.includes("sent") ? "success" : "error"}>{status}</StatusBanner>}
			{items.length === 0 && <p className="compact-empty-state">No previews have been requested yet.</p>}
			<ul className="preview-list">
				{items.map((item) => (
					<li key={item.id}>
						<span className="project-state">{item.status}</span>
						{item.status === "ready" && (
							<>
								<button type="button" onClick={() => launch(item.id)}>
									Open preview
								</button>
								<label htmlFor={`preview-feedback-${item.id}`}>Feedback on this preview</label>
								<textarea
									id={`preview-feedback-${item.id}`}
									value={feedback[item.id] ?? ""}
									onChange={(event) =>
										setFeedback((current) => ({ ...current, [item.id]: event.target.value }))
									}
								/>
								<button
									type="button"
									disabled={!feedback[item.id]?.trim()}
									onClick={() => sendFeedback(item.id)}
								>
									Send feedback
								</button>
							</>
						)}
					</li>
				))}
			</ul>
		</section>
	);
}
